import * as Device from "expo-device";
import * as Updates from "expo-updates";
import Constants from "expo-constants";
import { useCallback, useEffect, useRef, useState } from "react";

import { AppState, AppStateStatus, Platform } from "react-native";

import { getNotificationToken } from "./getNotificationToken";
import { useUser } from "../../OS/Login/State";
import { graphql, useMutation } from "react-relay";
import { useRegisterDeviceMutation } from "./__generated__/useRegisterDeviceMutation.graphql";
import { addBreadcrumb, captureException } from "../sentry";
import { useGetDeviceId } from "../analytics/getDeviceId";
import { DateTime } from "luxon";

export const useRegisterDevice = (skip: boolean) => {
  const user = useUser();
  const [appState, setAppState] = useState<AppStateStatus>("unknown");

  useEffect(() => {
    AppState.addEventListener("change", (state) => {
      setAppState(state);
    });
  }, []);

  const isForeground = appState === "active";
  const [registerDevice] = useMutation<useRegisterDeviceMutation>(graphql`
    mutation useRegisterDeviceMutation(
      $token: String
      $devicePlatform: DevicePlatform
      $deviceName: String
      $deviceModel: String
      $deviceId: String
      $buildVersion: String
      $updateVersion: String
      $versionUpdatedAt: String
    ) {
      me {
        registerDevice(
          token: $token
          devicePlatform: $devicePlatform
          deviceName: $deviceName
          deviceModel: $deviceModel
          deviceId: $deviceId
          buildVersion: $buildVersion
          updateVersion: $updateVersion
          versionUpdatedAt: $versionUpdatedAt
        )
      }
    }
  `);

  const [token, setToken] = useState<string | null | false>(false);

  const key = user.user.sub + ":" + user.currentStoreId + ":" + (token ?? "");

  useEffect(() => {
    let canceled = false;
    if (Platform.OS === "web") {
      return;
    }
    if (skip) {
      return;
    }
    getNotificationToken()
      .then((token) => {
        if (!canceled) {
          setToken(token.data);
        }
      })
      .catch((e) => {
        if (!canceled) {
          setToken((t) => (!t ? null : t));
        }
        console.log(e);
        console.log("Did not get notification token");
      });

    return () => {
      canceled = true;
    };
  }, [setToken, skip]);

  const deviceId = useGetDeviceId();
  const lastRequestRegisterDevice = useRef<DateTime | null>(null);
  const lastRequestKey = useRef<string | null>(null);
  const onRequestRegisterDevice = useCallback(
    (_token: string | null, _deviceId: string, _key: string) => {
      if (skip) {
        return;
      }
      const keyChanged = _key !== lastRequestKey.current;
      if (
        !keyChanged &&
        lastRequestRegisterDevice.current &&
        DateTime.now().diff(lastRequestRegisterDevice.current).as("minutes") < 5
      ) {
        return;
      }
      lastRequestRegisterDevice.current = DateTime.now();
      lastRequestKey.current = _key;

      registerDevice({
        variables: {
          token: _token,
          deviceName: Device.deviceName,
          devicePlatform:
            Platform.OS === "ios" ||
            Platform.OS === "android" ||
            Platform.OS === "web"
              ? Platform.OS
              : undefined,
          deviceModel: Device.modelName ?? Device.deviceYearClass + "",
          deviceId: _deviceId,
          buildVersion: Constants.expoConfig?.version,
          updateVersion: Updates.updateId,
          versionUpdatedAt: Updates.createdAt?.toISOString(),
        },
        onError: (e) => {
          captureException(e);
        },
        onCompleted: (data) => {
          addBreadcrumb({
            category: "notification",
            message: "Notification registered",
            data: {
              a: data.me.registerDevice,
            },
          });
        },
      });
    },
    [registerDevice, skip]
  );
  useEffect(() => {
    if (token !== false && deviceId) {
      onRequestRegisterDevice(token, deviceId, key);
    }
  }, [
    registerDevice,
    token,
    key,
    isForeground,
    deviceId,
    onRequestRegisterDevice,
  ]);

  /**
   * Guarantees to trigger heartbeat every 5 minutes even when the app is foregrounded for a long time,
   * to prevent the user from becoming inactive if no other action is triggering the heartbeat.
   */
  useEffect(() => {
    if (skip || !isForeground || token === false || !deviceId) {
      return;
    }
    const interval = setInterval(
      () => {
        onRequestRegisterDevice(token, deviceId, key);
      },
      5 * 60 * 1000
    );
    return () => clearInterval(interval);
  }, [skip, isForeground, token, deviceId, key, onRequestRegisterDevice]);

  return { token };
};
